import { TZDate } from '@date-fns/tz'
import { type Duration, parseISO, formatDuration as dateFnsFormatDuration, format } from 'date-fns'
import { OrganizationRole, Preferences, ProjectRole, organizationRoles, projectRoles } from './types'
import { v4 } from 'uuid'

export const createUuid = () => {
    return v4()
}

export const isNumber = (str: any) => {
    if (typeof str !== 'string' || str.trim() === '') return false
    return Number.isFinite(Number(str))
}

export const toInt = (str: string | undefined, defaultValue: number) => {
    const p = parseInt(str ?? '', 10)
    return isNaN(p) ? defaultValue : p
}

export function round(n: number, places?: number) {
    if (places && places > 0) {
        const f = Math.pow(10, places)
        return Math.round(n * f) / f
    }
    return Math.round(n)
}

/**
 * Abbreviates counts past four digits, which caps the rendered value at five
 * characters so an element sized for it never has to grow.
 */
export function formatCompactNumber(n: number) {
    return n < 10000
        ? n.toLocaleString()
        : n.toLocaleString(undefined, { notation: 'compact', maximumSignificantDigits: 3 })
}

export const prune = (obj: Record<string, any>): Record<string, any> => {
    return Object.fromEntries(
        Object.entries(obj)
            .filter(([_, v]) => v != null && v !== ''),
    )
}

export const toNaturalList = (items: string[], conjunction = 'or') => {
    if (items.length === 0) return ''
    if (items.length === 1) return items[0]
    if (items.length === 2) return `${items[0]} ${conjunction} ${items[1]}`

    const allButLast = items.slice(0, -1).join(', ')
    const last = items[items.length - 1]
    return `${allButLast} ${conjunction} ${last}`
}

export const snakeToTitle = (snake: string) => {
    return (snake ?? '').split('_').map(p => p.charAt(0).toUpperCase() + p.substring(1)).join(' ')
}

export const camelToTitle = (camel: string) => {
    return camel
        .replace(/([A-Z])/g, (match) => ` ${match}`)
        .replace(/^./, (match) => match.toUpperCase())
        .trim()
}

export const combine = (...parts: Array<string | number>) => {
    return parts.filter(item => item != null).join(' ')
}

export const deepMerge = <T extends Record<string, any>>(
    target: T,
    source?: Partial<T>,
): T => {
    const result = { ...target }

    if (!source) return target

    for (const key in source) {
        if (Object.prototype.hasOwnProperty.call(source, key)) {
            const sourceValue = source[key]
            const targetValue = result[key]

            if (sourceValue === null) continue
            if (targetValue === null) {
                result[key] = sourceValue as any
                continue
            }

            if (
                sourceValue
                && typeof sourceValue === 'object'
                && !Array.isArray(sourceValue)
                && targetValue
                && typeof targetValue === 'object'
                && !Array.isArray(targetValue)
            ) {
                result[key] = deepMerge(
                    targetValue as Record<string, any>,
                    sourceValue as Record<string, any>,
                ) as any
            } else {
                result[key] = sourceValue as any
            }
        }
    }
    return result
}

export function localStorageGetJson<T extends object>(key: string) {
    try {
        const stored = localStorage.getItem(key)
        if (stored) {
            return JSON.parse(stored) as T
        }
    } catch (err) {
        console.warn(err)
    }
}

export function localStorageSetJson<T extends object>(key: string, o: T) {
    localStorage.setItem(key, JSON.stringify(o))
}

export function debounce(fn: (...args: any[]) => any, ms = 300) {
    let timeoutId: ReturnType<typeof setTimeout>
    return function(this: any, ...args: any[]) {
        clearTimeout(timeoutId)
        timeoutId = setTimeout(() => fn.apply(this, args), ms)
    }
}

type DateArg = number | string | Date

function isDate(date: any): date is Date {
    return date instanceof Date && !isNaN(date.getTime())
}

function parseDate(date: DateArg) {
    if (typeof date === 'number') {
        return new Date(date)
    }
    if (typeof date === 'string') {
        let parsed = parseISO(date)
        if (isDate(parsed)) return parsed
        parsed = new Date(date)
        if (isDate(parsed)) return parsed
    }
    return date
}

const TIMEZONE_SUFFIX = /(Z|[+-]\d{2}(:?\d{2})?)$/i

export function parseUtcDate(value: unknown): Date | undefined {
    if (typeof value !== 'string' || !value.trim()) return undefined
    const [date, time = '00:00'] = value.trim().split(/[T ]/)
    const parsed = parseISO(`${date}T${TIMEZONE_SUFFIX.test(time) ? time : `${time}Z`}`)
    return isNaN(parsed.getTime()) ? undefined : parsed
}

export function formatDate(preferences: Preferences, date: DateArg, fmt: string = 'Pp', timeZone = preferences.timeZone) {
    return format(new TZDate(+parseDate(date), timeZone), fmt)
}

export function formatDuration(_preferences: Preferences, duration: Duration) {
    return dateFnsFormatDuration(duration, {
        delimiter: ', ',
        // TODO locale
    })
}

export function languageName(locale: string) {
    try {
        const languages = new Intl.DisplayNames([locale], {
            type: 'language',
        })
        return languages.of(locale)
    } catch {
        return undefined
    }
}

export function shiftArray<T>(arr: T[], shiftBy: number): T[] {
    const len = arr.length
    if (len === 0) return []

    const n = ((shiftBy % len) + len) % len
    return arr.slice(n).concat(arr.slice(0, n))
}

export function createComparator<T>(getter: (o: T) => any, desc = false) {
    return (a: T, b: T) => {
        const av = getter(a)
        const bv = getter(b)
        if (av < bv) {
            return desc ? 1 : -1
        }
        if (av > bv) {
            return desc ? -1 : 1
        }
        return 0
    }
}

export function groupBy<T>(arr: T[], fn: (item: T) => any) {
    return arr.reduce<Record<string, T[]>>((prev, curr) => {
        const groupKey = fn(curr)
        const group = prev[groupKey] || []
        group.push(curr)
        return { ...prev, [groupKey]: group }
    }, {})
}

export function groupByKey<T>(arr: T[], key: keyof T) {
    return groupBy(arr, (item) => item[key])
}

export function arrayMove<T>(arr: T[], currentIndex: number, targetIndex: number) {
    if (targetIndex >= arr.length) {
        let k = targetIndex - arr.length + 1
        while (k--) {
            (arr as any).push(undefined)
        }
    }
    arr.splice(targetIndex, 0, arr.splice(currentIndex, 1)[0])
    return arr
}

const RECENT_PROJECTS = 'recent-projects'

type RecentProjects = Array<{
    id: number
    when: number
}>

export function getRecentProjects() {
    return (localStorageGetJson<RecentProjects>(RECENT_PROJECTS) ?? [])
}

export function pushRecentProject(id: number | string) {
    const stored = getRecentProjects()
    const idx = stored.findIndex(p => p.id === id)
    if (idx !== -1) {
        arrayMove(stored, idx, 0)
    } else {
        stored.unshift({
            id: typeof id === 'string' ? parseInt(id, 10) : id,
            when: Date.now(),
        })
    }
    while (stored.length > 3) {
        stored.pop()
    }
    localStorageSetJson(RECENT_PROJECTS, stored)
    return stored
}

/**
 * @returns true if user has at least the minRole
 */
export function checkProjectRole(minRole: ProjectRole, currentRole: ProjectRole = 'support') {
    return projectRoles.indexOf(minRole) <= projectRoles.indexOf(currentRole)
}

export function checkOrganizationRole(minRole: OrganizationRole, currentRole: OrganizationRole = 'member') {
    return organizationRoles.indexOf(minRole) <= organizationRoles.indexOf(currentRole)
}

export type PathKey = string | number

export interface UpdateResult<T> {
    updated: T
    patch: Record<string, unknown> | { [key: number]: unknown }
}

export function updateValueAtPath<T extends readonly unknown[]>(
    obj: T,
    path: PathKey[],
    value: unknown,
): UpdateResult<T>
export function updateValueAtPath<T extends Record<string, unknown>>(
    obj: T,
    path: PathKey[],
    value: unknown,
): UpdateResult<T>
export function updateValueAtPath(
    obj: readonly unknown[] | Record<string, unknown>,
    path: PathKey[],
    value: unknown,
): UpdateResult<any> {
    if (path.length === 0) {
        return {
            updated: obj,
            patch: Array.isArray(obj) ? [...obj] : { ...obj },
        }
    }

    const [topLevelKey, ...rest] = path

    // Base case: set the leaf value immutably
    if (rest.length === 0) {
        if (Array.isArray(obj)) {
            if (typeof topLevelKey !== 'number') {
                throw new Error('Array indices must be numbers')
            }
            const newArr: unknown[] = obj.slice()
            newArr[topLevelKey] = value
            return {
                updated: newArr,
                patch: { [topLevelKey]: value },
            }
        } else {
            const newObj: Record<string, unknown> = { ...obj, [String(topLevelKey)]: value }
            return {
                updated: newObj,
                patch: { [String(topLevelKey)]: value },
            }
        }
    }

    // Recursive case: update nested value
    const currentChild = Array.isArray(obj)
        ? (typeof topLevelKey === 'number' ? obj[topLevelKey] : undefined)
        : (obj as Record<string, unknown>)[String(topLevelKey)]

    const shouldBeArray = typeof rest[0] === 'number'
    const fallbackChild = shouldBeArray ? [] : {}
    const childForUpdate = currentChild ?? fallbackChild
    const updatedChild = updateValueAtPath(childForUpdate, rest, value)

    // Rebuild immutably at this level
    if (Array.isArray(obj)) {
        if (typeof topLevelKey !== 'number') {
            throw new Error('Array indices must be numbers')
        }
        const newArr: unknown[] = obj.slice()
        newArr[topLevelKey] = updatedChild.updated
        return {
            updated: newArr,
            patch: { [topLevelKey]: updatedChild.updated },
        }
    } else {
        const newObj: Record<string, unknown> = {
            ...obj,
            [String(topLevelKey)]: updatedChild.updated,
        }
        return {
            updated: newObj,
            patch: { [String(topLevelKey)]: updatedChild.updated },
        }
    }
}

export const crc16 = (str: string) => {
    let crc = 0
    for (let i = 0; i < str.length; i++) {
        crc ^= str.charCodeAt(i) << 8
        for (let j = 0; j < 8; j++) {
            if (crc & 0x8000) {
                crc = (crc << 1) ^ 0x1021
            } else {
                crc = crc << 1
            }
            crc &= 0xFFFF
        }
    }
    return crc
}

export const redisShard = (key: string) => {
    return crc16(key) % 16384
}
